import { IUpdateBasketClientDTO } from '@common/interfaces/eventEntries/UpdateBasketEvent';
import { analyticsEventsDelegate } from './analyticsEventDelegate';
import { analyticsEvents } from './analyticsEvents';
import LogRocket from 'logrocket';
import { BasketItemType } from '@portal/types/nexudus';
import { EventEntryBasketItem } from '@common/interfaces/eventEntries/EventEntryBasketItem';
import { IStartCheckoutClientDTO } from '@common/interfaces/eventEntries/StartCheckoutEvent';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

const isBrowser = typeof window !== `undefined`;
// Ensure window.dataLayer is defined
if (isBrowser) {
  window.dataLayer = window.dataLayer || [];
}
class AnalyticsService {
  gtmTagId: string | null = null;

  initAnalytics(gtmTagId: string) {
    this.gtmTagId = gtmTagId;
  }

  trackGTMEvent(event: string, eventDetails: Record<string, unknown> = {}) {
    if (isBrowser) {
      window.dataLayer.push({
        event,
        ecommerce: eventDetails,
      });
    }
  }

  removeNestedObjects(obj: Record<string, unknown>) {
    return Object.keys(obj).reduce((acc, key) => {
      if (typeof obj[key] === 'object') {
        return { ...acc, [key]: JSON.stringify(obj[key]) };
      }
      return { ...acc, [key]: obj[key] };
    }, {});
  }

  mapBasketItems(items: EventEntryBasketItem[]) {
    return items.map((item) => ({
      item_id: String(item.item_id),
      item_name: item.item_name,
      item_category: item.item_category,
      price: item.price,
      quantity: item.quantity,
    }));
  }

  private mapAnalyticsItem(item: EventEntryBasketItem): EventEntryBasketItem {
    return {
      item_id: String(item.item_id),
      item_name: item.item_name,
      item_category: item.item_category as BasketItemType,
      price: item.price,
      quantity: item.quantity,
    };
  }

  private createUpdateBasketDTO(
    basketItems: EventEntryBasketItem[],
    item: EventEntryBasketItem,
    action: 'add' | 'remove',
  ): IUpdateBasketClientDTO {
    const updatedBasketItems =
      action === 'add'
        ? [...basketItems, item]
        : basketItems.filter(
            (basketItem) => basketItem.item_id !== item.item_id,
          );

    return {
      platform: 'web',
      timestamp: new Date().toISOString(),
      updatedItem: item,
      basketItems: updatedBasketItems,
      basketValue: this.calculateBasketValue(updatedBasketItems),
    };
  }

  calculateBasketValue(items: EventEntryBasketItem[]) {
    return items.reduce((total, item) => {
      return (
        total + (item.price ?? (0 as number)) * (item.quantity ?? (0 as number))
      );
    }, 0);
  }

  login = () => {
    this.trackGTMEvent(analyticsEvents.login);
    LogRocket.track(analyticsEvents.login);
  };

  pageView = ({ location }: { location: string }) => {
    // pageViews are tracked automatically by GTM already
    LogRocket.track(
      analyticsEvents.pageView,
      this.removeNestedObjects({
        page_location: location,
      }),
    );
  };

  viewItem(eventDetails) {
    this.trackGTMEvent(analyticsEvents.viewItem, eventDetails);
    LogRocket.track(
      analyticsEvents.viewItem,
      this.removeNestedObjects(eventDetails),
    );
  }

  addToBasket(eventDetails) {
    this.trackGTMEvent(analyticsEvents.addToCart, eventDetails);
    LogRocket.track(
      analyticsEvents.addToCart,
      this.removeNestedObjects(eventDetails),
    );

    const basketItems: EventEntryBasketItem[] = this.mapBasketItems(
      eventDetails.basketDetails.items as EventEntryBasketItem[],
    );
    const addedItem: EventEntryBasketItem = this.mapAnalyticsItem(
      eventDetails.items[0] as EventEntryBasketItem,
    );
    const updateBasketDTO: IUpdateBasketClientDTO = this.createUpdateBasketDTO(
      basketItems,
      addedItem,
      'add',
    );
    void analyticsEventsDelegate.updateBasket(updateBasketDTO);
  }

  removeFromBasket(eventDetails) {
    this.trackGTMEvent(analyticsEvents.removeFromCart, eventDetails);
    LogRocket.track(
      analyticsEvents.removeFromCart,
      this.removeNestedObjects(eventDetails),
    );

    const basketItems: EventEntryBasketItem[] = this.mapBasketItems(
      eventDetails.basketDetails?.items as EventEntryBasketItem[],
    );

    const removedItem: EventEntryBasketItem = this.mapAnalyticsItem(
      eventDetails.items[0] as EventEntryBasketItem,
    );

    const updateBasketDTO: IUpdateBasketClientDTO = this.createUpdateBasketDTO(
      basketItems,
      removedItem,
      'remove',
    );

    void analyticsEventsDelegate.updateBasket(updateBasketDTO);
  }

  checkoutBasket(eventDetails) {
    this.trackGTMEvent(analyticsEvents.beginCheckout, eventDetails);
    LogRocket.track(
      analyticsEvents.beginCheckout,
      this.removeNestedObjects(eventDetails),
    );

    const basketItems: EventEntryBasketItem[] = this.mapBasketItems(
      eventDetails.items as EventEntryBasketItem[],
    );

    const startCheckoutDTO: IStartCheckoutClientDTO = {
      platform: 'web',
      timestamp: new Date().toISOString(),
      invoiceValue: eventDetails.value as number,
      basketItems: basketItems,
    };
    void analyticsEventsDelegate.startCheckout(startCheckoutDTO);
  }

  clearBasket(eventDetails) {
    this.trackGTMEvent(analyticsEvents.clearCart, eventDetails);
    LogRocket.track(
      analyticsEvents.clearCart,
      this.removeNestedObjects(eventDetails),
    );
  }

  viewBasket(eventDetails) {
    this.trackGTMEvent(analyticsEvents.viewCart, eventDetails);
    LogRocket.track(
      analyticsEvents.viewCart,
      this.removeNestedObjects(eventDetails),
    );
  }

  signUp(eventDetails) {
    this.trackGTMEvent(analyticsEvents.signUp, eventDetails);
    LogRocket.track(
      analyticsEvents.signUp,
      this.removeNestedObjects(eventDetails),
    );
  }

  saved({
    agentKey,
    key,
    ...restDetails
  }: {
    agentKey: string;
    key: string;
    [key: string]: unknown;
  }) {
    this.trackGTMEvent(analyticsEvents.saved(agentKey, key), restDetails);
    LogRocket.track(
      analyticsEvents.saved(agentKey, key),
      this.removeNestedObjects(restDetails),
    );
  }

  purchase(eventDetails: Record<string, unknown>) {
    this.trackGTMEvent(analyticsEvents.purchase, eventDetails);
    LogRocket.track(
      analyticsEvents.purchase,
      this.removeNestedObjects(eventDetails),
    );
  }

  fail(eventDetails: Record<string, unknown>) {
    this.trackGTMEvent(analyticsEvents.fail, eventDetails);
    LogRocket.track(
      analyticsEvents.fail,
      this.removeNestedObjects(eventDetails),
    );
  }

  updated(eventDetails: Record<string, unknown>) {
    this.trackGTMEvent(analyticsEvents.updated, eventDetails);
    LogRocket.track(
      analyticsEvents.updated,
      this.removeNestedObjects(eventDetails),
    );
  }

  deletionModal(eventDetails: Record<string, unknown>) {
    this.trackGTMEvent(analyticsEvents.deletionModal, eventDetails);
    LogRocket.track(
      analyticsEvents.deletionModal,
      this.removeNestedObjects(eventDetails),
    );
  }

  // scripts to use google tag manager
  gtmHead = (gtmTagId: string) => {
    return (
      <script
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmTagId || this.gtmTagId}');`,
        }}
      />
    );
  };

  gtmNoHead = (gtmTagId: string) => {
    return (
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmTagId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    );
  };
}

export const analyticsService = new AnalyticsService();
